import React, {
	forwardRef, useEffect, useMemo, useState,
} from 'react';
import { UniqueIdentifier } from '@dnd-kit/core';
import { SortableContext, SortingStrategy } from '@dnd-kit/sortable';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import styles from './Container.module.scss';
import { ITag } from '../../../../../../entities/module/private-template-module-tag/api/types';
import { useGroupActions } from '../../hooks/useGroupActions';
import { Mods } from '../../../../../../shared/libs/classNames';
import { useCreatePrivateTemplateTag } from '../../../../tag/create-private-template-tag/useCreatePrivateTemplateTag';
import { templateTagGroupActions, templateTagGroupApi } from '../../../../../../entities/template/private-template-tag-group';
import { SortableItem } from '../multipleContainers/MultipleContainers';
import { ITagGroup, TagGroupType } from '../../../../../../entities/template/private-template-tag-group/model/types';
import ArrowIcon from '../../../../../../shared/assets/images/arrow-icon.svg';
import ReverseArrowIcon from '../../../../../../shared/assets/images/reverse-arrow-icon.svg';
import TrashIcon from '../../../../../tag-managment/images/trash.svg';
import { CreateTagDropdown } from '../../../../../tag-managment/create-tag-dropdown';
import {
	templateTagGroupSelectors,
} from '../../../../../../entities/template/private-template-tag-group/model/slice/privateTemplateTagGroupSlice';
import { useAppDispatch } from '../../../../../../shared/libs/redux';
import {
	CreateFirstGroupTag,
} from '../../../../../tag-managment/create-first-group-tag/CreateFirstGroupTag';
import { EditableGroupName } from '../editable-group-name';

export interface Props {
	columns?: number;
	label?: string;
	style?: React.CSSProperties;
	horizontal?: boolean;
	hover?: boolean;
	handleProps?: React.HTMLAttributes<any>;
	scrollable?: boolean;
	shadow?: boolean;
	placeholder?: boolean;
	unstyled?: boolean;
	onClick: (value: string) => void;
	onRemove?(): void;
	getIndex: (id: UniqueIdentifier) => number;
	group: ITagGroup,
	wrapperStyle({ index }: {index: number}): React.CSSProperties;
	strategy?: SortingStrategy;
	handle: boolean,
	selected?: boolean,
	getItemStyles?(args: {
		value: UniqueIdentifier;
		index: number;
		overIndex: number;
		isDragging: boolean;
		containerId: UniqueIdentifier;
		isSorting: boolean;
		isDragOverlay: boolean;
	}): React.CSSProperties;
	modalRef: React.RefObject<HTMLDivElement>;
}

export const Container = forwardRef<HTMLDivElement & HTMLButtonElement, Props>(({
	group,
	columns = 1,
	handleProps,
	horizontal,
	hover,
	onClick,
	onRemove,
	label,
	placeholder,
	style,
	scrollable,
	shadow,
	unstyled,
	getIndex,
	wrapperStyle,
	strategy,
	handle,
	selected = false,
	getItemStyles = () => ({}),
	modalRef,
	...props
}: Props, ref) => {
	const dispatch = useAppDispatch();
	const { id = TagGroupType.createGroup, name = '' } = group || {};
	const [tags, setTags] = useState<ITag[]>();
	const [areTagsVisible, setAreTagsVisible] = useState(false);
	const [rerenderFlag, setRerenderFlag] = useState(false);

	const openedIds = useSelector(templateTagGroupSelectors.getOpenedGroupsIds);

	const sortedTags = useMemo(
		() => (
			group.tags ? [...group.tags].sort((a, b) => a.sequenceNumber - b.sequenceNumber) : []
		),
		[group, group.tags],
	);

	useEffect(() => {
		setTags(sortedTags);

		if (openedIds.some(id => id === group.id)) {
			setAreTagsVisible(true);
		}
	}, [sortedTags]);

	const forceRerender = () => {
		setRerenderFlag(prev => !prev); // Меняем состояние, чтобы вызвать перерендер
	};

	const safeTags = Array.isArray(tags) ? tags : [];

	const {
		openDeleteGroupModal,
	} = useGroupActions(name, id);

	const mods: Mods = {
		[styles.open]: areTagsVisible,
		[styles.closed]: !areTagsVisible,
		[styles.selected]: selected,
		[styles.virtualGroup]: id === TagGroupType.createGroup,
	};

	const isCreateGroup = id === TagGroupType.createGroup;
	const isTagsWithoutGroup = id === TagGroupType.tagsWithoutGroup;

	const toggleBlockVisibility = () => {
		dispatch(templateTagGroupActions.setOpenedGroupsIds(id));
		if (isCreateGroup) {
			if (onClick) {
				onClick(id);
			}
		} else {
			setAreTagsVisible((prev) => !prev);
		}
	};

	const { onCreateTag } = useCreatePrivateTemplateTag();

	const [bindTag] = templateTagGroupApi.useBindMutation();

	return (
		<div
			{...props}
			ref={ref}
			style={{ ...style } as React.CSSProperties}
			className={classNames(
				styles.Container,
				unstyled && styles.unstyled,
				horizontal && styles.horizontal,
				hover && styles.hover,
				placeholder && styles.placeholder,
				scrollable && styles.scrollable,
				shadow && styles.shadow,
				styles.wrapper,
				mods,
			)}
			role="presentation"
		>

			<div className={styles.header}>
				{!isCreateGroup && (
					<div className={styles.icon} onClick={toggleBlockVisibility} role="presentation">
						{areTagsVisible ? <ReverseArrowIcon /> : <ArrowIcon />}
					</div>
				)}
				<EditableGroupName
					id={id}
					name={name}
					isCreateGroup={isCreateGroup}
					isTagsWithoutGroup={isTagsWithoutGroup}
				/>
				{!isCreateGroup && !isTagsWithoutGroup && (
					<div
						className={styles.icon}
						onClick={openDeleteGroupModal}
						role="presentation"
					>
						<TrashIcon />
					</div>
				)}
			</div>

			<SortableContext
				items={group.tags ? group.tags.map(tag => tag.id) : []}
				strategy={strategy}
			>
				{areTagsVisible
					&& (!tags?.length
						? (
							<CreateFirstGroupTag
								groupId={id.toString()}
								onCreateTag={onCreateTag}
								bindTag={bindTag}
								forceRerender={forceRerender}
								modalRef={modalRef}
							/>
						)
						: (
							<ul
								style={{
									display: areTagsVisible ? 'flex' : 'none',
									flexWrap: 'wrap',
									position: 'relative',
								}}
							>
								{sortedTags && sortedTags.map((value, index) => (
									<SortableItem
										tag={value}
										index={index}
										totalTags={safeTags.length}
										containerId={group.id}
										key={value.id}
										handle={handle}
										style={getItemStyles}
										wrapperStyle={wrapperStyle}
										getIndex={getIndex}
									/>
								))}

								{/* Иконка "+" для добавления тегов */}
								<CreateTagDropdown
									groupId={id.toString()}
									onCreateTag={onCreateTag}
									bindTag={bindTag}
									modalRef={modalRef}
								/>
							</ul>
						)
					)}
			</SortableContext>
		</div>
	);
});
